import React, { useEffect, useState } from "react";
import AdvancedSearchRow from "./AdvancedSearchRow";
import {
  AdvancedSearchFilterData,
  AdvancedSearchTextInputFilterData,
  advancedSearchAccessibility,
  advancedSearchPrimaryTarget,
  advancedSearchFiction,
  advancedSearchAgeGroups,
  advancedSearchMaterialTypes,
  AdvancedSearchQuery,
  initialAdvancedSearchQuery,
  advancedSearchChildrenoradults,
  advancedSearchSource,
  advancedSearchMood,
  advancedSearchGenreAndForm,
  advancedSearchSort,
  advancedSearchBranchFilter,
  advancedSearchPublicationYearGroups,
  advancedSearchLanguage,
  advancedSearchSublocationFilter,
} from "./types";
import { useText } from "../../core/utils/text";
import PreviewSection from "./PreviewSection";
import Multiselect from "../../components/multiselect/Multiselect";
import MultiselectWithInputs from "../../components/multiselect/MultiselectWithInputs";
import {
  MultiselectExternalUpdateFunction,
  MultiselectOption
} from "../../components/multiselect/types";
import CqlSearchHeader from "./CqlSearchHeader";
import {
  shouldAdvancedSearchButtonBeDisabled,
  translateSearchObjectToCql,
  // commaSeparatedStringToArray,
} from "./helpers";
// import {
//   removeQueryParametersFromUrl,
//   setQueryParametersInUrl
// } from "../../core/utils/helpers/url";
import { Button } from "../../components/Buttons/Button";
import CheckBox from "../../components/checkbox/Checkbox";
import { LocationFilter } from "./LocationFilter";
import { SortFilter } from "./SortFilter";
import Dropdown, { Option } from "../../components/Dropdown/Dropdown";
import TextInputFilter from "../../components/forms/input/TextInputFilter";
import { TextInputFilterExternalUpdateFunction } from "../../components/forms/input/types";
import { useStatistics } from "../../core/statistics/useStatistics";
// import { statistics } from "../../core/statistics/statistics";

export type AdvancedSearchHeaderProps = {
  dataCy?: string;
  searchQuery: string | null;
  setSearchQuery: (searchQuery: string | null) => void;
  searchObject: AdvancedSearchQuery | null;
  setSearchObject: (searchObject: AdvancedSearchQuery | null) => void;
  onShelf: boolean;
  setOnShelf: (checked: boolean) => void;
  onLocationChange: (location: string) => void;
  onSublocationChange: (sublocation: string) => void;
  onBranchChange: (branch: string) => void;
  onSortChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  //onPublicationYearChange: (publicationnYear: string) => void;
  // onLixChange: (lix: string) => void;
  locationFilter: LocationFilter;
  sortFilter: SortFilter;
};

const AdvancedSearchHeader: React.FC<AdvancedSearchHeaderProps> = ({
  dataCy = "advanced-search-header",
  searchQuery,
  setSearchQuery,
  searchObject,
  setSearchObject,
  onShelf,
  setOnShelf,
  onLocationChange,
  onSublocationChange,
  onBranchChange,
  onSortChange,
}) => {

  const [locationFilter, setLocationFilter] = useState<LocationFilter>({});

  const t = useText();
  const { track } = useStatistics();
  const [isFormMode, setIsFormMode] = useState<boolean>(true);
  // Keep an internal copy of the search object in a separate state. We only
  // want to update the outer state and perform a search when the user clicks
  // the search button.
  const [internalSearchObject, setInternalSearchObject] =
    useState<AdvancedSearchQuery>(
      searchObject || structuredClone(initialAdvancedSearchQuery)
    );
  const [previewCql, setPreviewCql] = useState<string>(searchQuery || "");
  const [rawCql, setRawCql] = useState<string>("");
  const [focusedRow, setFocusedRow] = useState<number | null>(null);

  const handleOnShelfChange = (checked: boolean) => {
    setOnShelf(checked);
  };

  // If a new search object is passed in, override the internal state to reflect
  // the updated values in the state.
  useEffect(() => {
    if (searchObject === null) return;
    setInternalSearchObject(searchObject);
  }, [searchObject]);

  useEffect(() => {
    const cql = translateSearchObjectToCql(internalSearchObject);
    setPreviewCql(cql);
  }, [internalSearchObject]);

  const reloadPage = () => {
    window.location.href = '/advanced-search';
  };

  const updateTextInputFiltersData = (textInputUpdate: { 
      key: keyof AdvancedSearchTextInputFilterData; 
      value: string; 
    }) => {
      const newSearchObject = { ...internalSearchObject };
      // Update the filter key with the new string value
      newSearchObject.textInputFilters = {
          ...newSearchObject.textInputFilters,
          [textInputUpdate.key]: textInputUpdate.value
      };
      setInternalSearchObject(newSearchObject);
    };

  const updateFiltersData = (filtersUpdate: {
    key: keyof AdvancedSearchFilterData;
    value: MultiselectOption[];
  }) => {
    if (!internalSearchObject?.filters[filtersUpdate.key].length) {
      return;
    }
    const newSearchObject = { ...internalSearchObject };
    newSearchObject.filters = {
      ...newSearchObject.filters,
      [filtersUpdate.key]: filtersUpdate.value
    };

    setInternalSearchObject(newSearchObject);
  };

  const reset = () => {
    setSearchObject(structuredClone(initialAdvancedSearchQuery));
  };
  const scrollToResults = () => {
    const element = document.getElementById("advanced-search-result");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  const handleSearchButtonClick = () => {
    if (rawCql.trim() !== "" && !isFormMode) {
      // track("click", {
      //   id: statistics.advancedSearchTerm.id,
      //   name: statistics.advancedSearchTerm.name,
      //   trackedData: rawCql
      // });
      setSearchQuery(rawCql);
      // Half a second makes sure search result is rendered before scrolling to it.
      setTimeout(() => {
        scrollToResults();
      }, 500);
      return;
    }
    // track("click", {
    //   id: statistics.advancedSearchTerm.id,
    //   name: statistics.advancedSearchTerm.name,
    //   trackedData: previewCql
    // });
    setSearchObject(internalSearchObject);
    // Half a second makes sure search result is rendered before scrolling to it.
    setTimeout(() => {
      scrollToResults();
    }, 500);
  };
  const [isSearchButtonDisabled, setIsSearchButtonDisabled] =
    useState<boolean>(true);
  const translatedCql = previewCql || searchQuery || "";
  // debugger;
  useEffect(() => {
    if (searchQuery && !searchObject) {
      setIsFormMode(false);
    }
  }, [searchObject, searchQuery]);

  useEffect(() => {
    setIsSearchButtonDisabled(
      shouldAdvancedSearchButtonBeDisabled(
        isFormMode,
        internalSearchObject,
        rawCql
      )
    );
  }, [internalSearchObject, rawCql, isFormMode]);

  // Empty but needed for updates to work. Fix or find way to remove
  const handleInputChange = (
    name: "publicationYear" | "lix" | "dk5" | "author" | "title" | "subject" | "let" | "creatorcontributor" | "ages",
    value: string
  ) => {};

  return (
    <>
      {isFormMode && (
        <>
          <div>
            <a className="advanced-search__backarrow flexing" href="/">
              <svg width="50" height="16" viewBox="0 0 40 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path className="arrow__body" d="M30 8H0" stroke="black" strokeWidth="2"></path>
                <path 
                  className="arrow__head" 
                  d="M0.293 7.2929C-0.098 7.6834 -0.098 8.3166 0.293 8.7071L6.657 15.0711C7.047 15.4616 7.681 15.4616 8.071 15.0711C8.462 14.6805 8.462 14.0474 8.071 13.6569L2.414 8.00001L8.071 2.3431C8.462 1.9526 8.462 1.3195 8.071 0.928976C7.681 0.538451 7.047 0.538451 6.657 0.928976L0.293 7.2929ZM8 7.00001H0V9.00001H8V7.00001Z"
                  fill="black">
                </path>
              </svg>
              <p className="advanced-search__backarrow-text">Til forsiden</p>
            </a>
          </div>
          <h1 className="text-header-h2 advanced-search__title capitalize-first">
            {t("advancedSearchTitleText")}
          </h1>
          <div className="input-and-preview">
            <div className="input-and-preview__input">
              {internalSearchObject.rows.map((row, index) => {
                return (
                  <AdvancedSearchRow
                    key={row.id}
                    data={internalSearchObject}
                    rowIndex={index}
                    setSearchObject={setInternalSearchObject}
                    dataCy={`${dataCy}-row`}
                    setFocusedRow={setFocusedRow}
                    isFocused={focusedRow === index}
                    onSearchButtonClick={handleSearchButtonClick}
                  />
                );
              })}
            </div>
            <PreviewSection
              translatedCql={translatedCql}
              reset={reloadPage}
              // reset={reset}
              setIsFormMode={setIsFormMode}
            />
          </div>

          <h3 className="advanced-search__filters-heading">{t("advancedSearchFilterHeadingText")}</h3>
          <section 
            className="advanced-search__filters"
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                handleSearchButtonClick();
              }
            }}
          >
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchFilterMaterialTypeText")}
                options={advancedSearchMaterialTypes}
                defaultValue={internalSearchObject.filters.materialTypes}
                updateExternalState={{
                  key: "materialTypes",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-material-types"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchFilterAccessText")}
                options={advancedSearchAccessibility}
                defaultValue={internalSearchObject.filters.accessibility}
                updateExternalState={{
                  key: "accessibility",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-accessibility"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchFilterLiteratureFormText")}
                options={advancedSearchFiction}
                defaultValue={internalSearchObject.filters.fiction}
                updateExternalState={{
                  key: "fiction",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-fiction"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchFilterLanguageText")}
                options={advancedSearchLanguage}
                defaultValue={internalSearchObject.filters.language}
                updateExternalState={{
                  key: "language",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-language"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchChildrenoradultsText")}
                options={advancedSearchChildrenoradults}
                defaultValue={internalSearchObject.filters.childrenoradults}
                updateExternalState={{
                  key: "childrenoradults",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-childrenoradults"
              />
            </div>
            <div className="advanced-search__filter"> 
              <MultiselectWithInputs
                caption={t("advancedSearchFilterAgeGroupText")}
                options={advancedSearchAgeGroups}
                defaultValue={internalSearchObject.filters.ageGroups}
                updateExternalState={{
                  key: "ageGroups",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                updateExternalStateInputs={{
                  key: "ages",
                  externalUpdateFunction:
                    updateTextInputFiltersData as TextInputFilterExternalUpdateFunction
                }}
                dataCy="advanced-search-age-group"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchFilterPrimaryTargetText")}
                options={advancedSearchPrimaryTarget}
                defaultValue={internalSearchObject.filters.primaryTarget}
                updateExternalState={{
                  key: "primaryTarget",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-primary-target"
              />
            </div>
            <div className="advanced-search__filter"> 
              <MultiselectWithInputs
                caption={t("advancedSearchFilterPublicationYearGroupText")}
                options={advancedSearchPublicationYearGroups}
                defaultValue={internalSearchObject.filters.publicationYear}
                updateExternalState={{
                  key: "publicationYear",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                updateExternalStateInputs={{
                  key: "publicationyear",
                  externalUpdateFunction:
                    updateTextInputFiltersData as TextInputFilterExternalUpdateFunction
                }}
                dataCy="advanced-search-publication-year-group"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchSourceText")}
                options={advancedSearchSource}
                defaultValue={internalSearchObject.filters.source}
                updateExternalState={{
                  key: "source",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-primary-target"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchFilterGenreAndFormText")}
                options={advancedSearchGenreAndForm}
                defaultValue={internalSearchObject.filters.genreandform}
                updateExternalState={{
                  key: "genreandform",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-mood"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchMoodText")}
                options={advancedSearchMood}
                defaultValue={internalSearchObject.filters.mood}
                updateExternalState={{
                  key: "mood",
                  externalUpdateFunction:
                    updateFiltersData as MultiselectExternalUpdateFunction
                }}
                dataCy="advanced-search-mood"
              />
            </div>
            <div className="advanced-search__filter">
              <Multiselect
                caption={t("advancedSearchFilterBranchText")}
                options={advancedSearchBranchFilter}
                defaultValue={internalSearchObject.filters.branch}
                updateExternalState={{
                  key: "branch",
                  externalUpdateFunction: (filtersUpdate) => {
                    const newSearchObject = { ...internalSearchObject };
                    setInternalSearchObject(newSearchObject);
                    // extract values from each branch object via arrow function and join 
                    // resulting array to csv string and feed to onBranchChange function:
                    if (Array.from(filtersUpdate.value).some(option => option.value !== "all")) {
                      // if (filtersUpdate.value.length > 0) {
                      setOnShelf(true);
                      onBranchChange(filtersUpdate.value.map(option => option.value).join(", "));
                    } else {
                      setOnShelf(false); // Check the on-shelf checkbox
                      onBranchChange('');
                    }
                  }
                }}
              />
            </div>
              {/* <div className="advanced-search__filter">
                <Multiselect
                  caption={t("advancedSearchFilterSublocationText")}
                  options={advancedSearchSublocationFilter}
                  defaultValue={internalSearchObject.filters.sublocation}
                  updateExternalState={{
                    key: "sublocation",
                    externalUpdateFunction: (filtersUpdate) => {
                      const newSearchObject = { ...internalSearchObject };
                      setInternalSearchObject(newSearchObject);
                      if (Array.from(filtersUpdate.value).some(option => option.value !== "all")) {
                        onSublocationChange(filtersUpdate.value.map(option => option.value).join(", "));
                      } else {
                        onSublocationChange('');
                      }
                    }
                  }}
                />
              </div> */}
              <div className="advanced-search__filter">
                  <TextInputFilter
                    id="lix"
                    label={t("advancedSearchFilterLixText")}
                    description={t("advancedSearchFilterLixDescriptionText")}
                    type="text"
                    onChange={(lix) => handleInputChange("lix", lix)}
                    // onSearchButtonClick={handleSearchButtonClick}
                    updateExternalState={{
                      key: "lix",
                      externalUpdateFunction:
                        updateTextInputFiltersData as TextInputFilterExternalUpdateFunction
                    }}
                  />
                </div>
                <div className="advanced-search__filter">
                  <TextInputFilter
                    id="let"
                    label={t("advancedSearchFilterLetText")}
                    description={t("advancedSearchFilterLetDescriptionText")}
                    type="text"
                    onChange={(lettal) => handleInputChange("let", lettal)} // TODO denne skal væk sammen med onChange i typen
                    // onSearchButtonClick={handleSearchButtonClick}
                    updateExternalState={{
                      key: "let",
                      externalUpdateFunction:
                        updateTextInputFiltersData as TextInputFilterExternalUpdateFunction
                    }}
                  />
                </div>
          </section>
          <CheckBox
            id="on-shelf"
            selected={onShelf}
            onChecked={handleOnShelfChange}
            label={t("advancedSearchFilterHoldingStatusOverrideText")}
          />
          <PreviewSection
            translatedCql={translatedCql}
            reset={reset}
            isMobile
            setIsFormMode={setIsFormMode}
          />
        </>
      )}
      {!isFormMode && (
        <CqlSearchHeader
          initialCql={translatedCql}
          setCql={setRawCql}
          onShelf={onShelf}
          handleOnShelfChange={handleOnShelfChange}
          onLocationChange={onLocationChange}
          onSublocationChange={onSublocationChange}
          onBranchChange={onBranchChange}
          // onSortChange={onSortChange}
          locationFilter={locationFilter}
        />
      )}

      <section className="advanced-search__footer">
        {!isFormMode && (
          <button
            type="button"
            className="link-tag advanced-search__back-button cursor-pointer"
            onClick={() => setIsFormMode(true)}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                setIsFormMode((prev) => !prev); // Toggles the state
              }
            }}
          >
            {t("toAdvancedSearchButtonText")}
          </button>
        )}
        <div className="search_button_and_sorting">
          <Button
            dataCy="search-button"
            buttonType="none"
            disabled={isSearchButtonDisabled}
            size="xlarge"
            variant="filled"
            classNames="advanced-search__search-button"
            collapsible
            label={t("advancedSearchSearchButtonText")}
            onClick={handleSearchButtonClick}
          /> 
          <div className="advanced-search__filter">
            <Dropdown
              classNames="dropdown--grey-borders"
              options={advancedSearchSort}
              arrowIcon="chevron"
              //defaultValue={"Bedste match"}
              handleOnChange={onSortChange}
              ariaLabel={t("advancedSearchSortText")}
              label={t("advancedSearchSortText")}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default AdvancedSearchHeader;


